import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAutomationsApiFacade } from '../../../../apis/facades/useAutomationsApiFacade';
import { useDocumentationApiFacade } from '../../../../apis/facades/useDocumentationApiFacade';
import { useRollupsApiFacade } from '../../../../apis/facades/useRollupsApiFacade';
import {
  setSelectedCardItem,
  setCardDependsOn,
  setCardUsedBy,
  setCardOpenAiDescription,
  setSourceCodeForConfiguration,
  setLatestDeployment,
  CardRelationshipMap,
} from '../../../../reducers/documentation/documentationCardReducer';
import { telemetry } from '../../../../telemetry';
import extractErrorMsg from '../../../helpers/extractErrorMsg';
import { SweepElementTypes } from '../../types';
import { formatOpenAiResponse } from '../helpers';
import { DocumentationCardItem } from '../types';
import { UserInputsByConfigurationId } from '@server/user-inputs';
import { useUserInputsApiFacade } from '../../../../apis/facades/useUserInputsApiFacade';
import { useLayouts } from '../../hooks/useLayouts';
import useParser from '../../../parser/useParser';
import { setSingleConfigurationUserInputs } from '../../../../reducers/documentation/userInputsReducer';
import { setRelatedListsAndButtons } from '../../../../reducers/documentation/salesforceDocumentationReducer';
import { selectParsedObjects } from '../../../../reducers/global/parserReducer';
import { LAYOUT, OBJECT } from '@server/parser';
import {
  SWEEP_AUTOMATION,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  SWEEP_ALERT,
  SWEEP_MATCHING,
  SWEEP_DEDUPE,
  ROLLUP,
  SalesforceItem,
  SalesforceItemLayout,
} from '../../../parser/ConfigurationItemTypes';
import { createConfigurationItemFromParserResponseItem } from '../../../parser/parserUtils';
import { ConsumersOrDependencies, ElementProperties } from '@server/documentation';
import { FIELD } from '@server/parser';

/**
 * Transforms API response into grouped items for the card reducer.
 * Returns items grouped by configuration type.
 */
const transformToCardFormat = (values: ConsumersOrDependencies): CardRelationshipMap => {
  const cardItems: CardRelationshipMap = {};

  Object.keys(values).forEach((key) => {
    const _values = values as any;
    const arr: SalesforceItem[] =
      _values[key]?.map((item: ElementProperties) =>
        createConfigurationItemFromParserResponseItem({
          responseItem: item,
          objectApiName: item.objectName ?? '',
          keyType: key,
        }),
      ) ?? [];

    if (key === FIELD) {
      cardItems[key] = arr.map((item) => ({ ...item, parentType: FIELD }));
    } else {
      cardItems[key] = arr;
    }
  });

  return cardItems;
};

export const useSalesforceCard = (crmOrgId: string) => {
  const dispatch = useDispatch();

  const {
    post_crmOrgGenerateConfigurationGpt,
    get_crmOrgConfigurationConsumersFromSalto,
    get_crmOrgConfigurationDependenciesFromSalto,
    get_crmOrgFieldConsumerFromSalto,
    get_NaclFile,
  } = useDocumentationApiFacade();

  const { put_userInputsForConfiguration } = useUserInputsApiFacade();

  const { get_automation } = useAutomationsApiFacade();
  const { get_rollup } = useRollupsApiFacade();
  const { fetchLayoutData } = useLayouts();
  const { parseObjectOnDemand } = useParser({ crmOrgId });

  const parsedObjects = useSelector(selectParsedObjects(crmOrgId));

  const handleSetConfigurationItem = useCallback(
    async (props: DocumentationCardItem) => {
      const { item, contentType, clearHistory } = props;
      const { parentType, metadataId, objectApiNames } = item as SalesforceItem;
      const _item = { ...item } as SalesforceItem;

      const parsedObjectNames = parsedObjects?.map((_item) => _item.name);

      if (parentType === LAYOUT && metadataId) {
        const isLayoutObjectParsed = parsedObjectNames?.some(
          (objectName: string) => objectName === objectApiNames?.[0],
        );
        if (!isLayoutObjectParsed) {
          fetchLayoutData(metadataId);
        } else {
          const layoutItem = _item as SalesforceItemLayout;
          dispatch(
            setRelatedListsAndButtons({
              metadataId,
              lists: layoutItem.relatedLists ?? [],
              buttons: layoutItem.buttons ?? [],
            }),
          );
        }
      }

      if (parentType === OBJECT) {
        if (!parsedObjectNames?.some((name: string) => name === objectApiNames?.[0])) {
          await parseObjectOnDemand(_item.name);
        }
      }

      dispatch(
        setSelectedCardItem({
          platform: 'salesforce',
          item: _item,
          contentType,
          clearHistory,
        }),
      );
    },
    [dispatch, fetchLayoutData, parseObjectOnDemand, parsedObjects],
  );

  const getOpenAiDescriptionForRule = useCallback(
    async ({ metadataId, crmOrgId }: { crmOrgId: string; metadataId: string }) => {
      try {
        const res = await post_crmOrgGenerateConfigurationGpt({
          crmOrgId,
          payload: { metadataId },
        });
        const openAiDescription = formatOpenAiResponse(res);

        dispatch(setCardOpenAiDescription({ metadataId, description: openAiDescription }));
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
        dispatch(
          setCardOpenAiDescription({
            metadataId,
            description: {
              type: 'error',
              text: 'Something went wrong, please try again later',
            },
          }),
        );
      }
    },
    [dispatch, post_crmOrgGenerateConfigurationGpt],
  );

  const getConfigurationDependencies = useCallback(
    async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
      try {
        const dependsOn = await get_crmOrgConfigurationDependenciesFromSalto({
          crmOrgId,
          metadataId,
        });

        const cardItems = transformToCardFormat(dependsOn);
        dispatch(setCardDependsOn({ metadataId, items: cardItems }));
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgConfigurationDependenciesFromSalto],
  );

  const getConfigurationUsedBy = useCallback(
    async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
      try {
        const usedBy = await get_crmOrgConfigurationConsumersFromSalto({
          crmOrgId,
          metadataId,
        });

        const cardItems = transformToCardFormat(usedBy);
        dispatch(setCardUsedBy({ metadataId, items: cardItems }));
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgConfigurationConsumersFromSalto],
  );

  const getFieldConsumers = useCallback(
    async ({
      objectName,
      fieldName,
      crmOrgId,
      metadataId,
    }: {
      objectName: string;
      fieldName: string;
      crmOrgId: string;
      metadataId: string;
    }) => {
      try {
        const usedBy = await get_crmOrgFieldConsumerFromSalto({
          crmOrgId,
          objectName,
          fieldName,
          metadataId,
        });

        const cardItems = transformToCardFormat(usedBy);
        dispatch(setCardUsedBy({ metadataId, items: cardItems }));
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgFieldConsumerFromSalto],
  );

  const fetchLatestDeployedVersion = useCallback(
    async (sweepElementType: SweepElementTypes, elementId: string, crmOrgId: string) => {
      let element: any;

      switch (sweepElementType) {
        case SWEEP_AUTOMATION:
        case SWEEP_ASSIGNMENT:
        case SWEEP_SCHEDULED_ASSIGNMENT:
        case SWEEP_ALERT:
        case SWEEP_MATCHING:
        case SWEEP_DEDUPE:
          element = await get_automation(elementId, crmOrgId, true);
          break;
        case ROLLUP:
          element = await get_rollup(elementId, crmOrgId, true);
          break;
        default:
          break;
      }

      if (element.readVersion) {
        dispatch(setLatestDeployment({ elementId, readOnlyElement: element.readVersion }));
        return;
      } else {
        telemetry.captureError(
          new Error('Read version was not returned for crmOrgId and element '),
          {
            crmOrgId,
            elementId,
          },
        );
        return;
      }
    },
    [dispatch, get_automation, get_rollup],
  );

  const updateDependencyTags = useCallback(
    (userInputs: UserInputsByConfigurationId, notParsedObjects?: string[]) => {
      try {
        dispatch(setSingleConfigurationUserInputs({ userInputs, crmOrgId }));
        put_userInputsForConfiguration({ crmOrgId, userInputs });

        if (notParsedObjects && notParsedObjects.length > 0) {
          notParsedObjects.forEach((objectName) => parseObjectOnDemand(objectName));
        }
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [crmOrgId, put_userInputsForConfiguration, dispatch, parseObjectOnDemand],
  );

  const getSourceCode = useCallback(
    async (metadataId: string, configurationId: string) => {
      try {
        const sourceCode = await get_NaclFile({ crmOrgId, metadataId });
        dispatch(setSourceCodeForConfiguration({ crmOrgId, sourceCode, configurationId }));
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [crmOrgId, get_NaclFile, dispatch],
  );

  return {
    handleSetConfigurationItem,
    getOpenAiDescriptionForRule,
    updateDependencyTags,
    getConfigurationUsedBy,
    getConfigurationDependencies,
    getFieldConsumers,
    fetchLatestDeployedVersion,
    getSourceCode,
  };
};
